import { useContext, useEffect, useState, useMemo, useCallback } from 'react';
import appContext from '../../context/appContext';
import { fetchData } from '../../data/fetchData';
import CartProducts from './CartProducts';
import Logistics from '../../UI/Logistics';
import Payment from '../../UI/Payment';
import style from './Cart.module.css';
import DateTimePicker from '../../UI/DateTimePicker';
import { NavLink, useNavigate } from 'react-router-dom';

const getDeliveryDate = () => {
    let today = new Date();

    // const isNewYear31 = today.toDateString() === new Date(2025, 11, 31).toDateString();
    // const isNewYear = today.toDateString() === new Date(2026, 0, 1).toDateString();
    const dayX = today.toDateString() === new Date(2026, 2, 8).toDateString();
    if (dayX) today = new Date(2026, 2, 9);

    const hours = today.getHours();
    if (hours >= 21) {
        today.setDate(today.getDate() + 1);
    }
    return today.toLocaleDateString('uk-UA', { year: 'numeric', month: '2-digit', day: '2-digit' }).split('.').reverse().join('-');
};

const getDeliveryTiming = () => {
    const today = new Date();
    const isNewYear = today.toDateString() === new Date(2025, 11, 31).toDateString() || today.toDateString() === new Date(2026, 0, 1).toDateString();
    if (isNewYear) return 'timed';
    const hours = today.getHours();
    if (hours >= 21 || hours < 10) {
        return 'timed';
    }
    return 'nearest';
};

const getInitialFormData = ({ phone, address, deliveryOption, location, comment, payment }) => ({
    phone,
    address,
    deliveryOption,
    location,
    deliveryDate: getDeliveryDate(),
    minDeliveryDate: getDeliveryDate(),
    deliveryTiming: getDeliveryTiming(),
    timeInterval: '',
    payment,
    paid: false,
    comment,
    noCallback: true,
    birthday: false,
    finalSum: 0,
    discount: 0,
});

function Cart({ newOrderUrl, getZoneUrl, initData, cities }) {
    const { cart, setCart, locations, related, customer, repeatOrderParams, setRepeatOrderParams, checkoutConfig } = useContext(appContext);
    const [accepted, setAccepted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [clientSelected, setClientSelected] = useState(false);

    const navigate = useNavigate();

    const initParams = repeatOrderParams
        ? {
              deliveryOption: repeatOrderParams.deliveryOption,
              location: repeatOrderParams.location,
              address: repeatOrderParams.address,
              comment: repeatOrderParams.comment,
              phone: repeatOrderParams?.phone || '',
              payment: repeatOrderParams?.payment || 'cash',
          }
        : {
              deliveryOption: customer.deliveryOption,
              location: customer.location || locations[0]?.id || '',
              address: customer.address,
              comment: '',
              phone: customer?.contact?.phone || '',
              payment: customer?.identified ? 'liqpay' : 'cash',
          };

    useEffect(() => {
        if (repeatOrderParams) {
            setRepeatOrderParams(null);
        }
    }, [repeatOrderParams, setRepeatOrderParams]);

    const [formData, setFormData] = useState(getInitialFormData(initParams));

    const addRelatedProducts = useCallback(
        (products) => {
            products.forEach((relatedProduct) => {
                const existsInCart = cart.some((item) => item.id === relatedProduct.id);
                if (!existsInCart) {
                    setCart((prevCart) => [
                        ...prevCart,
                        {
                            id: relatedProduct.id,
                            price: relatedProduct.price,
                            amount: 0,
                            sum: 0,
                            imgUrl: relatedProduct.imgUrl,
                            title: relatedProduct.title,
                            related: true,
                            discountForbidden: true,
                        },
                    ]);
                }
            });
        },
        [cart, setCart],
    );

    useEffect(() => {
        if (!!customer && customer.identified) setClientSelected(true);
    }, [customer]);

    useEffect(() => {
        if (cart.length > 0) {
            addRelatedProducts(related.chopsticks);
            addRelatedProducts(related.sauces);
        }

        const updateComment = (currentComment, amount, label) => {
            if (amount === 0) {
                return currentComment.includes(label) ? currentComment : currentComment ? `${currentComment} ${label}` : label;
            } else {
                return currentComment.replace(label, '').trim();
            }
        };

        const chopsticksAmount = cart.reduce(
            (amount, item) => (related.chopsticks.some((chopstick) => chopstick.id === item.id) ? amount + item.amount : amount),
            0,
        );
        const sauceAmount = cart.reduce((amount, item) => (item.id === '000000002' ? amount + item.amount : amount), 0);
        const gingerAmount = cart.reduce((amount, item) => (item.id === '000000006' ? amount + item.amount : amount), 0);
        const wasabiAmount = cart.reduce((amount, item) => (item.id === '000000007' ? amount + item.amount : amount), 0);

        setFormData((prev) => {
            let newComment = prev.comment || '';
            newComment = updateComment(newComment, chopsticksAmount, 'без паличок');
            newComment = updateComment(newComment, sauceAmount, 'без соєвого соуса');
            newComment = updateComment(newComment, gingerAmount, 'без імбиру');
            newComment = updateComment(newComment, wasabiAmount, 'без васабі');

            if (prev.comment !== newComment) {
                return { ...prev, comment: newComment };
            }
            return prev;
        });
    }, [cart, related.chopsticks, related.sauces, addRelatedProducts]);

    const { newSum, newAmount, customerDiscount, newFinalSum } = useMemo(() => {
        let customerDiscount = 0;

        const forbiddenDate = [new Date('2025-02-14'), new Date('2026-03-08'), new Date('2025-12-31')];
        const currentDate = new Date();
        const deliveryDate = new Date(formData.deliveryDate);
        const isForbiddenDate = forbiddenDate.some((date) => deliveryDate.getTime() === date.getTime());

        if (formData.deliveryOption === 'pickup') {
            customerDiscount = customer.customerStatus === 'regular' ? 20 : 15;
        } else {
            if (!isForbiddenDate) {
                if (formData.birthday) {
                    customerDiscount = customer.customerStatus === 'regular' ? 15 : 10;
                } else if (formData.deliveryTiming !== 'nearest' && formData.timeInterval) {
                    const fiveHoursLater = new Date(currentDate.getTime() + 5 * 3600 * 1000);
                    const [hours, minutes] = formData.timeInterval.split('-')[0].split(':').map(Number);
                    deliveryDate.setHours(hours, minutes, 0, 0);
                    if (deliveryDate > fiveHoursLater) {
                        customerDiscount = customer.customerStatus === 'regular' ? 10 : 7;
                    }
                }
            }
        }

        const newSum = cart.reduce((sum, item) => (item.related ? sum : sum + item.sum), 0);
        const newAmount = cart.reduce((amount, item) => amount + item.amount, 0);

        const newFinalSum = cart.reduce((sum, item) => {
            if (item.related) return sum;
            if (customerDiscount === 0 || item.discountForbidden) return sum + item.sum;
            if (Math.trunc(item.discount) >= Math.trunc(customerDiscount)) {
                return sum + item.sum;
            } else {
                const itemDiscount = item.discount !== 0 ? Math.max(customerDiscount, item.discount) : customerDiscount;
                return sum + Math.trunc(item.noDiscountPrice * item.amount * (1 - itemDiscount / 100));
            }
        }, 0);

        return { newSum, newAmount, customerDiscount, newFinalSum };
    }, [cart, customer, formData.deliveryOption, formData.birthday, formData.deliveryTiming, formData.deliveryDate, formData.timeInterval]);

    const totalSum = newSum;
    const finalSum = newFinalSum;
    const discount = customerDiscount;
    const amount = newAmount;

    const validationError = useMemo(() => {
        if (!formData.phone) {
            return 'Вкажіть телефон';
        }

        if (formData.phone.length < 10) {
            return 'Номер телефону повинен містити щонайменше 10 цифр';
        }

        const normalizeDate = (dateInput) => {
            const d = new Date(dateInput);
            d.setHours(0, 0, 0, 0);
            return d;
        };

        const deliveryDate = normalizeDate(formData.deliveryDate);

        const eventDay = normalizeDate(new Date(2026, 2, 8));

        if (eventDay.getTime() === deliveryDate.getTime()) {
            return `На жаль замовлення ${eventDay.toLocaleDateString('uk-UA')} більше не приймаються`;
            // if (formData.deliveryOption === 'delivery') {
            // }
            // if (formData.deliveryOption === 'pickup') {

            // }
        }

        if (cities && formData?.address?.city) {
            const city = cities.find((item) => item.id === formData.address.city);
            if (city) {
                const minDeliveryAmount = city.minAmount ?? 0;

                if (minDeliveryAmount > 0 && finalSum < minDeliveryAmount) {
                    return `Мінімальна сума замовлення для ${city.description} ${minDeliveryAmount}грн.`;
                }
            }
        }

        if (checkoutConfig?.minAmountDate) {
            const { amount: minAmount, date } = checkoutConfig.minAmountDate;

            if (date === formData.deliveryDate && minAmount > finalSum) {
                return `Мінімальна сума замовлення ${date} ${minAmount}грн.`;
            }
        }

        for (const item of cart) {
            if (item.related) continue;
            // nonCookingDays
            if (Array.isArray(item.nonCookingDays)) {
                for (const dateString of item.nonCookingDays) {
                    if (normalizeDate(dateString).getTime() === normalizeDate(deliveryDate).getTime()) {
                        const dateStr = deliveryDate.toLocaleDateString('uk-UA');
                        return `На жаль, "${item.title}" не готується ${dateStr}.`;
                    }
                }
            }

            // blockedDays
            if (Array.isArray(item.blockedDays) && item.blockedDays.includes(deliveryDate.getDay())) {
                const dateStr = deliveryDate.toLocaleDateString('uk-UA');
                return `На жаль, "${item.title}" не готується ${dateStr}.`;
            }

            if (item.availableTime?.startTime && item.availableTime?.endTime) {
                const toMinutes = (t) => {
                    const [h, m] = t.split(':').map(Number);
                    return h * 60 + m;
                };
                const start = toMinutes(item.availableTime.startTime);
                const end = toMinutes(item.availableTime.endTime);
                // availableTime (для timed)
                if (formData.deliveryTiming === 'timed' && formData.timeInterval) {
                    const [startStr, endStr] = formData.timeInterval.split('-');
                    const selectedStart = toMinutes(startStr);
                    const selectedEnd = toMinutes(endStr);

                    if (selectedStart < start || selectedEnd > end) {
                        return `На жаль, "${item.title}" готується з ${item.availableTime.startTime} до ${item.availableTime.endTime}.`;
                    }
                }

                // availableTime (для nearest)
                //Мінус година на доставку , мінус півгодини на самовивіз
                if (formData.deliveryTiming === 'nearest') {
                    const now = new Date();
                    const currentMinutes = now.getHours() * 60 + now.getMinutes();

                    let offset = 0;

                    // Минус час на доставку, минус 30 минут на самовывоз
                    if (formData.deliveryOption === 'pickup') {
                        offset = 30;
                    } else if (formData.deliveryOption === 'delivery') {
                        offset = 60;
                    }

                    const nearestTime = currentMinutes + offset;

                    if (nearestTime < start || nearestTime > end) {
                        return `На жаль, "${item.title}" готується з ${item.availableTime.startTime} до ${item.availableTime.endTime}.`;
                    }
                }
            }
        }

        return '';
    }, [
        cart,
        cities,
        checkoutConfig,
        finalSum,
        formData.phone,
        formData.deliveryDate,
        formData.deliveryTiming,
        formData.deliveryOption,
        formData.timeInterval,
        formData?.address?.city,
    ]);

    const handleFormChange = ({ target: { name, value, type, checked } }) => {
        if (name.startsWith('address.')) {
            const addressKey = name.split('.')[1];
            setFormData((prev) => ({
                ...prev,
                address: { ...prev.address, [addressKey]: value },
            }));
        } else if (type === 'checkbox') {
            setFormData((prev) => ({ ...prev, [name]: checked }));
        } else if (name === 'phone') {
            let val = value.replace(/[^\d+]/g, ''); // оставляем только цифры и '+'
            if (val.length > 13) val = val.slice(0, 13);
            value = val;
            setFormData((prev) => ({ ...prev, [name]: value }));
        } else {
            setFormData((prev) => ({ ...prev, [name]: value }));
        }
        if (name === 'deliveryOption' && value === 'pickup') {
            setFormData((prev) => ({ ...prev, birthday: false }));
        }

        if (name === 'deliveryDate' && value === '2025-03-08') {
            setFormData((prev) => ({ ...prev, birthday: false }));
        }

        setError('');
    };

    const validateForm = () => {
        let errorMessage = '';
        if (formData.deliveryOption === 'delivery') {
            if (!formData.address.city) {
                errorMessage += 'Місто не може бути порожнім.\n';
            }
            if (!formData.address.street) {
                errorMessage += 'Вулиця не може бути порожньою.\n';
            }
            if (!formData.address.house) {
                errorMessage += 'Номер будинку не може бути порожнім.\n';
            }
        }
        if (formData.deliveryTiming === 'timed') {
            if (!formData.timeInterval) {
                errorMessage += 'Не обрано час доставки.\n';
            }
        }

        return errorMessage;
    };

    const handleSubmit = async () => {
        // e.preventDefault();
        const validationError = validateForm();
        if (validationError) {
            setError(validationError);
            return;
        } else {
            setError('');
        }

        const isPaying = formData.payment === 'liqpay';
        //const products = cart.map(({ products, ...rest }) => ({ ...rest, products: products.map(({ imgURL, ...nestedRest }) => nestedRest) }));
        const products = cart.map(({ imgUrl, ...rest }) => rest);
        const orderData = { ...formData, products, finalSum, discount, initData, isPaying };

        setLoading(true);
        try {
            const responseData = await fetchData(newOrderUrl, 'POST', orderData);
            if (responseData.success) {
                setAccepted(true);
                setCart([]);
                if (isPaying) {
                    if (responseData.paymentData) {
                        navigate('/payment', { state: { paymentData: responseData.paymentData } });
                    } else {
                        setError('Сталася помилка при отриманні даних для оплати. ' + responseData.error);
                    }
                }
            } else {
                setError(responseData.error || 'Помилка при створенні замовлення. Спробуйте ще раз.');
            }
        } catch (error) {
            setError('Сталася помилка при відправці замовлення. Будь ласка, спробуйте ще раз..' + (error.message || ''));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={style.container}>
            {accepted ? <h2>Замовлення оформлене!</h2> : <h2>ОФОРМЛЕННЯ ЗАМОВЛЕННЯ</h2>}
            {amount === 0 ? (
                !accepted ? (
                    <p>Ваш кошик порожній</p>
                ) : (
                    ''
                )
            ) : (
                <>
                    <h3>ТОВАРИ В КОШИКУ</h3>
                    <CartProducts cart={cart} setCart={setCart} />
                    <form>
                        <Logistics address={formData.address} handleFormChange={handleFormChange} deliveryOption={formData.deliveryOption} />
                        <DateTimePicker formData={formData} handleFormChange={handleFormChange} getZoneUrl={getZoneUrl} />
                        <Payment handleFormChange={handleFormChange} clientSelected={clientSelected} currentPayment={formData.payment} />
                        <textarea
                            id="comment"
                            name="comment"
                            value={formData.comment}
                            onChange={handleFormChange}
                            rows="4"
                            className={style.commentInput}
                            placeholder="Ви можете додати свій коментар до замовлення"
                        />

                        <input
                            type="tel"
                            id="phone"
                            name="phone"
                            placeholder="+380XX XXX XX XX"
                            value={formData.phone}
                            onChange={handleFormChange}
                            className={style.phone}
                            disabled={clientSelected}
                            maxLength="13"
                            required
                        />

                        <div className={style.checkboxGroup}>
                            <input
                                type="checkbox"
                                id="noCallback"
                                name="noCallback"
                                className={style.checkboxNoCallback}
                                checked={formData.noCallback}
                                onChange={handleFormChange}
                            />
                            <label htmlFor="noCallback" className={style.labelNoCallback}>
                                Не дзвонити
                            </label>
                        </div>
                        {formData.deliveryOption !== 'pickup' && formData.deliveryDate !== '2026-03-08' && (
                            <div className={style.checkboxGroupBirthday}>
                                <input type="checkbox" id="birthday" name="birthday" checked={formData.birthday} onChange={handleFormChange} />
                                <label htmlFor="birthday" className={style.labelBirthday}>
                                    <span className={`${style.customCheckbox} ${formData.birthday ? style.customCheckboxChecked : ''}`}>
                                        {formData.birthday && <span className={style.checkmark}>✔</span>}
                                    </span>
                                    У мене день народження
                                </label>
                            </div>
                        )}
                        {error && <p className={style.error}>{error}</p>}
                        {validationError && <p className={style.error}>{validationError}</p>}

                        {totalSum !== finalSum && (
                            <>
                                <h2>Разом {totalSum.toFixed(2)} ₴</h2>
                                <h2>Знижка {discount} %</h2>
                            </>
                        )}
                        <h2>До сплати {finalSum.toFixed(2)} ₴</h2>
                        {!validationError && (
                            <button type="button" disabled={loading} className={style.btnSubmit} onClick={handleSubmit}>
                                {loading ? 'Завантаження...' : 'Оформити замовлення'}
                            </button>
                        )}
                    </form>
                </>
            )}
            {accepted && (
                <>
                    <p>Дякуємо за замовлення!❤️ Його деталі Ви завжди можете переглянути в розділі</p>
                    <NavLink to="/orders" className={style.myOrders}>
                        «Мої замовлення ✅»
                    </NavLink>
                </>
            )}
        </div>
    );
}

export default Cart;
